import { Link, useRouterState } from '@tanstack/react-router';
import { Radar, MessagesSquare, Users, Bell, User, Sparkles } from 'lucide-react';
import type { BadgeCounts } from '@web/shared';
import { cn } from '@/lib/cn';

/** The unread-count keys on `BadgeCounts` (excludes the boolean `proActive`). */
type BadgeKey = 'whispers' | 'circles' | 'alerts' | 'pro';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Radar;
  exact: boolean;
  badge?: BadgeKey;
}

const BASE: NavItem[] = [
  { to: '/app', label: 'Radar', icon: Radar, exact: true },
  { to: '/app/whispers', label: 'Whispers', icon: MessagesSquare, exact: false, badge: 'whispers' },
  { to: '/app/circles', label: 'Circles', icon: Users, exact: false, badge: 'circles' },
  { to: '/app/notifications', label: 'Alerts', icon: Bell, exact: false, badge: 'alerts' },
];
const PRO_ITEM: NavItem = {
  to: '/app/pro',
  label: 'Pro',
  icon: Sparkles,
  exact: false,
  badge: 'pro',
};
const YOU_ITEM: NavItem = { to: '/app/me', label: 'You', icon: User, exact: false };

function Count({ n, className }: { n: number; className?: string }) {
  if (n <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent px-1 text-[0.6rem] font-bold leading-none text-on-accent tabular-nums',
        className,
      )}
      aria-label={`${n} unread`}
    >
      {n > 9 ? '9+' : n}
    </span>
  );
}

/**
 * The signed-in app's primary nav. Two shapes off one model:
 * - `variant="bar"` — the fixed bottom tab bar (phones / tablets).
 * - `variant="rail"` — a vertical sidebar list (desktop `lg+`, in `AppShell`).
 * `showPro` inserts the Pro tab for members with an active grant.
 */
export function AppNav({
  counts,
  showPro = false,
  variant = 'bar',
  className,
}: {
  counts?: Partial<BadgeCounts>;
  showPro?: boolean;
  variant?: 'bar' | 'rail';
  className?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const items = showPro ? [...BASE, PRO_ITEM, YOU_ITEM] : [...BASE, YOU_ITEM];

  if (variant === 'rail') {
    return (
      <nav className={cn('flex flex-col gap-1', className)}>
        {items.map(({ to, label, icon: Icon, exact, badge }) => {
          const active = exact ? pathname === to : pathname.startsWith(to);
          const n = badge ? (counts?.[badge] ?? 0) : 0;
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-3 rounded-inset px-3 py-2 text-sm font-semibold transition-colors',
                active ? 'bg-u-soft text-u-ink' : 'text-ink-soft hover:bg-surface-sunken',
              )}
            >
              <Icon className="size-5 shrink-0" strokeWidth={active ? 2.5 : 2} />
              <span className="flex-1">{label}</span>
              <Count n={n} />
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className={cn('shrink-0 border-t-2 border-ink bg-canvas/95 backdrop-blur-md', className)}>
      <ul className="mx-auto flex max-w-md items-stretch justify-between px-1">
        {items.map(({ to, label, icon: Icon, exact, badge }) => {
          const active = exact ? pathname === to : pathname.startsWith(to);
          const n = badge ? (counts?.[badge] ?? 0) : 0;
          return (
            <li key={to} className="flex-1">
              <Link
                to={to}
                className={cn(
                  'flex flex-col items-center gap-0.5 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] text-[0.68rem] font-semibold transition-colors',
                  active ? 'text-u-ink' : 'text-ink-faint hover:text-ink',
                )}
              >
                <span
                  className={cn(
                    'relative inline-flex items-center justify-center rounded-pill px-3 py-0.5 transition-colors',
                    active && 'bg-u-soft',
                  )}
                >
                  <Icon className="size-5" strokeWidth={active ? 2.5 : 2} />
                  {n > 0 && (
                    <Count n={n} className="absolute -right-0.5 -top-1 border-2 border-canvas" />
                  )}
                </span>
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
